/**
 * Parse contract events from Blockscout Explorer transaction data.
 * Extracts InvoiceCreated, InvoicePaid, InvoiceCancelled from transaction logs.
 * Connected wallets = all unique tx.from (users who ever interacted with the contract), not just from decoded logs.
 */

import type { ExplorerTransaction, ExplorerTransactionsResponse } from '@/services/explorer-api';
import { MEZO_CONTRACTS } from '@/lib/mezo';

const INVOICE_CONTRACT_LOWER = MEZO_CONTRACTS.INVOICE_CONTRACT.toLowerCase();
const ETH_ADDR = /^0x[a-fA-F0-9]{40}$/;

export interface ParsedInvoiceEvent {
  id: number;
  creator: string;
  recipient?: string;
  amount?: string;
  type: 'created' | 'paid' | 'cancelled';
  txHash: string;
  timestamp: number;
}

export interface ExplorerStats {
  totalInvoices: number;
  paidCount: number;
  pendingCount: number;
  cancelledCount: number;
  uniqueCreators: string[];
  totalVolumeWei: bigint;
  pendingAmountWei: bigint;
  events: ParsedInvoiceEvent[];
}

function getInput(log: { decoded?: { name: string; inputs: Array<{ name: string; value: string }> }; topics: string[] }, name: string): string | undefined {
  const decoded = log.decoded;
  if (decoded?.inputs) {
    const found = decoded.inputs.find((i) => i.name === name);
    return found?.value;
  }
  return undefined;
}

function parseLog(
  log: { decoded?: { name: string; inputs: Array<{ name: string; value: string }> }; topics: string[] },
  tx: ExplorerTransaction
): ParsedInvoiceEvent | null {
  const name = log.decoded?.name;
  if (!name) return null;

  const ts = tx.timestamp ? new Date(tx.timestamp).getTime() / 1000 : 0;

  if (name === 'InvoiceCreated') {
    const id = getInput(log, 'id');
    const creator = getInput(log, 'creator');
    const recipient = getInput(log, 'recipient');
    const amount = getInput(log, 'amount');
    if (id == null || !creator) return null;
    return {
      id: parseInt(id, 10),
      creator,
      recipient: recipient ?? creator,
      amount,
      type: 'created',
      txHash: tx.hash,
      timestamp: Math.floor(ts),
    };
  }

  if (name === 'InvoicePaid') {
    const id = getInput(log, 'id');
    const amount = getInput(log, 'amount');
    if (id == null) return null;
    return {
      id: parseInt(id, 10),
      creator: tx.from?.hash ?? '',
      amount,
      type: 'paid',
      txHash: tx.hash,
      timestamp: Math.floor(ts),
    };
  }

  if (name === 'InvoiceCancelled') {
    const id = getInput(log, 'id');
    if (id == null) return null;
    return {
      id: parseInt(id, 10),
      creator: tx.from?.hash ?? '',
      type: 'cancelled',
      txHash: tx.hash,
      timestamp: Math.floor(ts),
    };
  }

  return null;
}

/**
 * Parse Explorer transactions response into invoice events and stats.
 */
export function parseExplorerTransactions(
  response: ExplorerTransactionsResponse | null
): ExplorerStats {
  const events: ParsedInvoiceEvent[] = [];
  const creators = new Set<string>();
  const createdIds = new Set<number>();
  const paidIds = new Set<number>();
  const cancelledIds = new Set<number>();

  const items = response?.items ?? [];
  if (!items.length) {
    return {
      totalInvoices: 0,
      paidCount: 0,
      pendingCount: 0,
      cancelledCount: 0,
      uniqueCreators: [],
      totalVolumeWei: 0n,
      pendingAmountWei: 0n,
      events: [],
    };
  }

  let totalVolumeWei = 0n;
  let pendingAmountWei = 0n;
  const createdAmounts = new Map<number, string>();

  for (const tx of items) {
    const logs = tx.logs ?? [];
    for (const log of logs) {
      const ev = parseLog(log, tx);
      if (!ev) continue;
      events.push(ev);
      
      // Only count invoice creators (not all transaction senders)
      // This ensures we only count wallets that actually created invoices
      if (ev.type === 'created' && ev.creator && ETH_ADDR.test(ev.creator)) {
        creators.add(ev.creator.toLowerCase());
        createdIds.add(ev.id);
        if (ev.amount) createdAmounts.set(ev.id, ev.amount);
      }
      if (ev.type === 'paid') {
        paidIds.add(ev.id);
        if (ev.amount) {
          try {
            totalVolumeWei += BigInt(ev.amount);
          } catch {
            /* ignore */
          }
        }
      }
      if (ev.type === 'cancelled') cancelledIds.add(ev.id);
    }
  }

  const pendingIds = [...createdIds].filter(
    (id) => !paidIds.has(id) && !cancelledIds.has(id)
  );
  const pendingCount = pendingIds.length;
  for (const id of pendingIds) {
    const amt = createdAmounts.get(id);
    if (amt) {
      try {
        pendingAmountWei += BigInt(amt);
      } catch {
        /* ignore */
      }
    }
  }

  // Sort by activity: wallets with more created invoices first
  const invoiceCountByCreator = new Map<string, number>();
  for (const ev of events) {
    if (ev.type === 'created' && ev.creator) {
      const lower = ev.creator.toLowerCase();
      invoiceCountByCreator.set(lower, (invoiceCountByCreator.get(lower) ?? 0) + 1);
    }
  }
  const uniqueCreatorsSorted = Array.from(creators).sort(
    (a, b) => (invoiceCountByCreator.get(b) ?? 0) - (invoiceCountByCreator.get(a) ?? 0)
  );

  const stats = {
    totalInvoices: createdIds.size,
    paidCount: paidIds.size,
    pendingCount,
    cancelledCount: cancelledIds.size,
    uniqueCreators: uniqueCreatorsSorted,
    totalVolumeWei,
    pendingAmountWei,
    events,
  };

  console.log('[event-parser] Parsed Explorer stats:', {
    totalInvoices: stats.totalInvoices,
    paidCount: stats.paidCount,
    pendingCount: stats.pendingCount,
    cancelledCount: stats.cancelledCount,
    uniqueCreators: stats.uniqueCreators.length,
    totalTransactions: items.length,
    eventsParsed: events.length,
  });

  return stats;
}
